import React, { useState, useEffect } from 'react';
import { DownloadSimple, FacebookLogo, TwitterLogo, InstagramLogo } from '@phosphor-icons/react';
import type { Verse } from '../../../features/bible/types';
import { Modal } from './Modal';
import { Spinner } from '../common/Spinner';

interface ShareVerseModalProps {
  isOpen: boolean;
  onClose: () => void;
  verse: Verse;
  verseText: string;
}

export function ShareVerseModal({ isOpen, onClose, verse, verseText }: ShareVerseModalProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && !imageUrl && !isLoading) {
      generateImage();
    }
  }, [isOpen]);

  const generateImage = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch('/api/bible/verses/share-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          verseText,
          book: verse.book,
          chapter: verse.chapter,
          verse: verse.verse,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate image');
      }

      const data = await response.json();
      setImageUrl(data.url);
    } catch (err) {
      setError('Failed to generate image. Please try again.');
      console.error('Error generating image:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  };

  const handleSocialShare = (platform: 'facebook' | 'twitter' | 'instagram') => {
    if (!imageUrl) return;

    const text = `${verse.book} ${verse.chapter}:${verse.verse} - ${verseText}`;
    const url = imageUrl;

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'instagram':
        // Instagram doesn't have a web sharing API, so we'll just download the image
        handleDownload();
        break;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Share Verse"
      variant="dialog"
      className="max-w-4xl"
    >
      <div className="flex flex-col items-center space-y-8">
        {error ? (
          <div className="text-red-500 text-center">
            <p>{error}</p>
            <button
              onClick={generateImage}
              className="mt-4 px-6 py-2 bg-[#361E06] text-white rounded-lg hover:bg-[#4A2B0B] text-sm font-medium"
            >
              Try Again
            </button>
          </div>
        ) : isLoading ? (
          <div className="text-center space-y-4">
            <Spinner variant="border" size="lg" className="text-[#361E06]" />
            <p className="text-[#361E06] font-medium">Generating your image...</p>
          </div>
        ) : imageUrl && (
          <>
            <div className="w-full">
              <img 
                src={imageUrl} 
                alt="Generated verse" 
                className="w-full rounded-lg shadow-lg"
              />
            </div>
            
            <div className="w-full grid grid-cols-2 gap-4">
              <button
                onClick={handleDownload}
                className="col-span-2 flex items-center justify-center gap-2 px-6 py-3 bg-[#361E06] text-white rounded-lg hover:bg-[#4A2B0B] font-medium"
              >
                <DownloadSimple className="w-5 h-5" weight="bold" />
                Download Image
              </button>
              
              <button
                onClick={() => handleSocialShare('facebook')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-[#1877F2] text-white rounded-lg hover:bg-[#0C63D4] font-medium"
              >
                <FacebookLogo className="w-5 h-5" weight="fill" />
                Facebook
              </button>
              
              <button
                onClick={() => handleSocialShare('twitter')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-[#333] font-medium"
              >
                <TwitterLogo className="w-5 h-5" weight="fill" />
                X
              </button>
              
              <button
                onClick={() => handleSocialShare('instagram')}
                className="col-span-2 flex items-center justify-center gap-2 px-6 py-3 bg-[#E4405F] text-white rounded-lg hover:bg-[#D32D4A] font-medium"
              >
                <InstagramLogo className="w-5 h-5" weight="fill" />
                Save for Instagram
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
} 